import { Component, OnInit } from '@angular/core';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { API } from 'src/environments/environment';
import * as XLSX from 'xlsx';

interface LinhaCentroCusto {
  codigo: number;
  idCentroCusto: number;
  centroCusto: string;
  descricao: string;
  creditos: number[];
  debitos: number[];
}

type LinhaExcel = Array<string | number>;

@Component({
  selector: 'app-analise-centro-custos',
  templateUrl: './analise-centro-custos.component.html',
  styleUrls: ['./analise-centro-custos.component.scss']
})
export class AnaliseCentroCustosComponent implements OnInit {
  ano = new Date().getFullYear();
  meses: string[] = [];
  totaisCreditos: number[] = [];
  totaisDebitos: number[] = [];
  linhas: LinhaCentroCusto[] = [];

  arrastandoTabela = false;
  private posicaoInicialX = 0;
  private scrollInicialX = 0;

  constructor(
    private ajaxService: AjaxService,
    private util: UtilService
  ) {}

  ngOnInit(): void {
    this.carregarRelatorio();
  }

  carregarRelatorio(): void {
    this.util.showLoading();
    this.ajaxService.get(`${API}financeiro/AnaliseCentroCustos/Relatorio?ano=${this.ano}`).subscribe({
      next: (response) => {
        this.util.closeLoading();
        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response?.message || 'Não foi possível carregar o relatório.');
          return;
        }
        const data = this.desempacotarResponse(response);
        this.meses = this.extrairLista(data, ['meses']).map((x: any) => String(x || ''));
        this.totaisCreditos = this.normalizarNumeros(this.extrairLista(data, ['totaiscreditos', 'totaisCreditos']));
        this.totaisDebitos = this.normalizarNumeros(this.extrairLista(data, ['totaisdebitos', 'totaisDebitos']));
        this.linhas = this.normalizarLinhas(this.extrairLista(data, ['linhas']));
      },
      error: () => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Falha ao carregar análise de centros de custos.');
      }
    });
  }

  alterarAno(delta: number): void {
    this.ano += delta;
    this.carregarRelatorio();
  }

  formatMoney(valor: number): string {
    return Number(valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  get totalAnoCreditos(): number {
    return this.somar(this.totaisCreditos);
  }

  get totalAnoDebitos(): number {
    return this.somar(this.totaisDebitos);
  }

  get saldoAno(): number {
    return this.totalAnoCreditos - this.totalAnoDebitos;
  }

  exportarExcel(): void {
    const cabecalho: LinhaExcel = ['Código', 'Centro de Custos'];

    this.meses.forEach((mes) => {
      cabecalho.push(`${mes} - Créditos`);
      cabecalho.push(`${mes} - Débitos`);
    });

    cabecalho.push('Total Créditos', 'Total Débitos');

    const linhasExcel: LinhaExcel[] = [cabecalho];

    const linhaTotais: LinhaExcel = ['', 'Totais'];
    (this.totaisCreditos || []).forEach((credito, idx) => {
      linhaTotais.push(Number(credito || 0));
      linhaTotais.push(Number(this.totaisDebitos[idx] || 0));
    });
    linhaTotais.push(this.somar(this.totaisCreditos), this.somar(this.totaisDebitos));
    linhasExcel.push(linhaTotais);

    (this.linhas || []).forEach((item) => {
      const linha: LinhaExcel = [Number(item.codigo || 0), String(item.centroCusto || '')];
      (item.creditos || []).forEach((credito, idx) => {
        linha.push(Number(credito || 0));
        linha.push(Number(item.debitos?.[idx] || 0));
      });
      linha.push(this.somar(item.creditos), this.somar(item.debitos));
      linhasExcel.push(linha);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(linhasExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'AnaliseCentroCustos');
    XLSX.writeFile(workbook, `analise-centro-custos-${this.ano}.xlsx`);
  }

  iniciarArrasteTabela(event: MouseEvent, container: HTMLElement): void {
    if (event.button !== 0) return;
    const alvo = event.target as HTMLElement;
    if (alvo?.closest('button, a, input, select, textarea, label')) return;
    this.arrastandoTabela = true;
    this.posicaoInicialX = event.clientX;
    this.scrollInicialX = container.scrollLeft;
  }

  moverArrasteTabela(event: MouseEvent, container: HTMLElement): void {
    if (!this.arrastandoTabela) return;
    const deslocamento = event.clientX - this.posicaoInicialX;
    container.scrollLeft = this.scrollInicialX - deslocamento;
    event.preventDefault();
  }

  encerrarArrasteTabela(): void {
    this.arrastandoTabela = false;
  }

  private desempacotarResponse(response: any): any {
    const candidatos = [
      response,
      response?.data,
      response?.data?.data,
      response?.data?.data?.data
    ].filter((x) => x && typeof x === 'object');

    const escolhido =
      candidatos.find((item: any) =>
        Array.isArray(item?.meses) ||
        Array.isArray(item?.linhas) ||
        Array.isArray(item?.totaisCreditos) ||
        Array.isArray(item?.totaiscreditos)
      ) || candidatos[candidatos.length - 1] || {};

    return this.util.converterParaLowercase(escolhido ?? {});
  }

  private extrairLista(payload: any, chaves: string[]): any[] {
    const fonte = this.util.converterParaLowercase(payload || {});
    for (const chave of chaves) {
      const valorDireto = fonte[chave];
      if (Array.isArray(valorDireto)) return valorDireto;
      const valorData = fonte?.data?.[chave];
      if (Array.isArray(valorData)) return valorData;
    }
    return [];
  }

  private normalizarNumeros(lista: any): number[] {
    if (!Array.isArray(lista)) return [];
    return lista.map((x: any) => Number(x || 0));
  }

  private somar(lista: number[]): number {
    return (lista || []).reduce((acc, val) => acc + Number(val || 0), 0);
  }

  private normalizarLinhas(lista: any): LinhaCentroCusto[] {
    if (!Array.isArray(lista)) return [];
    return lista.map((item: any, idx: number) => {
      const obj = this.util.converterParaLowercase(item || {});
      return {
        codigo: Number(obj.codigo || idx + 1),
        idCentroCusto: Number(obj.idcentrocusto || 0),
        centroCusto: String(obj.centrocusto || ''),
        descricao: String(obj.descricao || ''),
        creditos: this.normalizarNumeros(obj.creditos),
        debitos: this.normalizarNumeros(obj.debitos)
      } as LinhaCentroCusto;
    });
  }
}
